import type { ConsentRecord } from '@salonomy/types';
import { Modal, Pill } from '@salonomy/ui';
import { dateFormat } from '../../lib/locale.js';
import { CONSENT_KIND_LABEL, recordValidity } from './consent-shared.js';

const DATE_TIME_FMT = dateFormat({
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const DATE_FMT = dateFormat({
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

/** Render the frozen `answers` blob (filled fields) as plain label/value rows. Best-effort: the field
 *  schema is `unknown`, so we show whatever flat key/value pairs an object carries and skip anything else. */
function AnswerRows({ answers }: { answers: unknown }) {
  if (!answers || typeof answers !== 'object' || Array.isArray(answers)) return null;
  const entries = Object.entries(answers as Record<string, unknown>).filter(
    ([, v]) => v != null && v !== '',
  );
  if (entries.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
      {entries.map(([k, v]) => (
        <div key={k} className="till__line" style={{ fontSize: 13 }}>
          <span className="muted">{k}</span>
          <span>{typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v)}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Read-only viewer for ONE captured consent record. The headline surface is the FROZEN verbatim
 * snapshot (snapshotTitle/snapshotBody) — the liability wording reproduced exactly as it was signed,
 * never the (possibly later-edited) live template. Shows the typed-name attestation, when/how it was
 * signed, the filled answers, and a patch-test validity badge derived from the server's validUntil.
 */
export function ConsentRecordModal({
  record,
  onClose,
}: {
  record: ConsentRecord;
  onClose: () => void;
}) {
  const validity = recordValidity(record);

  return (
    <Modal open onClose={onClose} title={record.snapshotTitle || 'Consent record'} width={560}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        <Pill tone="neutral">{CONSENT_KIND_LABEL[record.kind]}</Pill>
        {validity === 'valid' ? (
          <Pill tone="success">Valid</Pill>
        ) : validity === 'expired' ? (
          <Pill tone="danger">Expired</Pill>
        ) : null}
      </div>

      {/* The liability surface — the wording the client agreed to, frozen at sign time. Pre-wrapped so
          the verbatim copy reproduces line breaks exactly. */}
      <div className="client-detail__block">
        <span className="slm-stat__label">Wording signed (frozen snapshot)</span>
        <div
          style={{
            fontSize: 13.5,
            marginTop: 6,
            lineHeight: 1.55,
            whiteSpace: 'pre-wrap',
            maxHeight: 280,
            overflowY: 'auto',
          }}
        >
          {record.snapshotBody || <span className="muted">No wording recorded.</span>}
        </div>
      </div>

      <AnswerRows answers={record.answers} />

      <div className="client-detail__block" style={{ marginTop: 14 }}>
        <span className="slm-stat__label">Attestation</span>
        <div className="settings-list" style={{ marginTop: 6 }}>
          <div className="settings-row">
            <div className="muted" style={{ fontSize: 12.5 }}>
              Signed by (typed name)
            </div>
            <div style={{ fontWeight: 600 }}>{record.signedName}</div>
          </div>
          <div className="settings-row">
            <div className="muted" style={{ fontSize: 12.5 }}>
              Signed at
            </div>
            <div>{DATE_TIME_FMT.format(new Date(record.signedAt))}</div>
          </div>
          <div className="settings-row">
            <div className="muted" style={{ fontSize: 12.5 }}>
              Captured via
            </div>
            <div>
              {record.signedVia === 'console_staff'
                ? 'Staff at the salon'
                : record.signedVia === 'client_portal'
                  ? 'Client self-serve'
                  : 'Online booking'}
            </div>
          </div>
          {record.validUntil ? (
            <div className="settings-row">
              <div className="muted" style={{ fontSize: 12.5 }}>
                Valid until
              </div>
              <div>{DATE_FMT.format(new Date(`${record.validUntil}T00:00:00Z`))}</div>
            </div>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
